// client/src/pages/MyProfile.jsx
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Grid, CircularProgress,
    Tabs, Tab, IconButton, Stack
} from '@mui/material';
import {
    Copy, Check,
    Eye, EyeOff, User,
    ShieldAlert, Grid3X3
} from 'lucide-react';
import { employeeService } from '../services/employeeService';

const MyProfile = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [copiedField, setCopiedField] = useState(null);
    const [showSalary, setShowSalary] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await employeeService.getMyProfile();
                setProfile(response.data);
            } catch (err) {
                console.error("Data fetch failed", err);
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleCopy = (text, fieldName) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 2000);
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f3f2f1' }}>
            <CircularProgress thickness={4} size={40} sx={{ color: '#0176d3' }} />
        </Box>
    );

    if (!profile) return (
        <Box sx={{ p: 4, bgcolor: '#f3f2f1', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ maxWidth: 440, textAlign: 'center', p: 4, borderRadius: '4px', bgcolor: '#ffffff', border: '1px solid #ea001e', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <ShieldAlert size={48} color="#ea001e" style={{ margin: '0 auto 16px' }} />
                <Typography variant="h6" sx={{ color: '#181818', fontWeight: 700, mb: 1 }}>
                    An error occurred while loading this page
                </Typography>
                <Typography variant="body2" sx={{ color: '#444444', lineHeight: 1.5 }}>
                    The record could not be loaded. Please check your system administrator permissions.
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Box sx={{
            width: '100%',
            minHeight: '100vh',
            bgcolor: '#f3f2f1', // Authentic SLDS workspace background grey
            color: '#181818',
            fontFamily: '"Salesforce Sans", Arial, sans-serif',
            display: 'flex',
            flexDirection: 'column'
        }}>

            {/* ========================================== */}
            {/* 1. SALESFORCE GLOBAL BLUE HEADER SYSTEM      */}
            {/* ========================================== */}
            <Box sx={{
                bgcolor: '#1b5eae', // Classic Salesforce Dark Blue Global Bar
                height: '55px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                color: '#ffffff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                zIndex: 1100
            }}>
                {/* Left: App Launcher & Logo */}
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton sx={{ color: '#ffffff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                        <Grid3X3 size={20} />
                    </IconButton>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: '0.02em', fontSize: '1rem', cursor: 'default' }}>
                        My Profile
                    </Typography>

                </Stack>

            </Box>

            {/* ========================================== */}
            {/* 3. LIGHTNING WORKSPACE CONTENT FRAME         */}
            {/* ========================================== */}
            <Box sx={{ p: 3, flex: 1, maxWidth: '1600px', width: '100%', mx: 'auto', boxSizing: 'border-box' }}>

                {/* --- HIGHLIGHT RECORD HERO PANEL --- */}
                <Box sx={{
                    bgcolor: '#ffffff',
                    border: '1px solid #dddbda',
                    borderRadius: '4px 4px 4px 4px',
                    p: 2.5,
                    mb: 3,
                    boxShadow: '0 2px 2px 0 rgba(0, 0, 0, 0.05)'
                }}>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                            {/* Iconic Salesforce Standard Orange/Crimson Record Object Avatar Frame Container */}
                            <Box sx={{ bgcolor: '#f2cf5b', p: 1.2, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.1)' }}>
                                <User size={32} color="#ffffff" strokeWidth={2.5} />
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm sx={{ pl: 1 }}>
                            <Typography variant="caption" sx={{ color: '#444444', fontWeight: 600, display: 'block', fontSize: '0.75rem', lineHeight: 1 }}>
                                Employee Profile
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#081c3b', mt: 0.5, fontSize: '1.4rem' }}>
                                {profile.name}
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Highlights Strip (Sub-metrics Row) */}
                    <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #f3f2f1' }}>
                        <Grid container spacing={3}>
                            <SalesforceMiniField label="Designation" value={profile.role} />
                            <SalesforceMiniField label="Email Id" value={profile.email} isCopyable onCopy={() => handleCopy(profile.email, 'email')} isCopied={copiedField === 'email'} />
                            <SalesforceMiniField label="Phone Number" value={profile.phone} isCopyable onCopy={() => handleCopy(profile.phone, 'phone')} isCopied={copiedField === 'phone'} />
                            <SalesforceMiniField label="Work Location" value={profile.office_location || 'Remote Node'} />
                        </Grid>
                    </Box>
                </Box>

                {/* --- MAIN RECORD TABS & CONTENT ROW DESIGN --- */}
                <Grid container spacing={3}>

                    {/* LEFT COLUMN DETAIL CONTAINER AREA */}
                    <Grid item xs={12} lg={8}>
                        {/* Salesforce Sub-tab Layout Selection Matrix */}
                        <Box sx={{ borderBottom: '1px solid #dddbda', mb: 2, bgcolor: '#ffffff', borderRadius: '4px 4px 0 0', px: 2 }}>
                            <Tabs
                                value={activeTab}
                                onChange={(e, val) => setActiveTab(val)}
                                sx={{
                                    minHeight: 'auto',
                                    '& .MuiTabs-indicator': { bgcolor: '#0176d3', height: 3 },
                                    '& .MuiTab-root': {
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '0.85rem',
                                        color: '#444444',
                                        minWidth: 'auto',
                                        mr: 4,
                                        px: 1,
                                        pb: 1.2,
                                        pt: 1.5,
                                        '&.Mui-selected': { color: '#0176d3', fontWeight: 700 }
                                    }
                                }}
                            >
                                <Tab label="Details" />
                                <Tab label="Financials & Clearance" />
                            </Tabs>
                        </Box>

                        {/* Active Workspace View Block Router */}
                        {activeTab === 0 && (
                            <Stack spacing={3}>
                                <SalesforceCardBlock title="Employment Specification Parameters">
                                    <Grid container spacing={3}>
                                        <SalesforceStandardField label="Employee ID" value={`#${profile.id}`} />
                                        <SalesforceStandardField label="Joining Date" value={profile.date_of_joining} />
                                        <SalesforceStandardField label="Reporting Manager" value={profile.reporting_manager} />
                                        <SalesforceStandardField label="Previous Experience" value={profile.previous_experience_years ? `${profile.previous_experience_years} Years` : 'Zero Reference Basis'} />
                                        <SalesforceStandardField label="Education Qualification" value={profile.education_qualification} />
                                        <SalesforceStandardField label="Date of Birth" value={profile.dob} />
                                        <SalesforceStandardField label="Permanent Address" value={profile.home_address} />
                                    </Grid>
                                </SalesforceCardBlock>
                            </Stack>
                        )}

                        {activeTab === 1 && (
                            <Stack spacing={3}>
                                <SalesforceCardBlock title="Disbursement Allocation Specs">
                                    <Grid container spacing={3}>
                                        <SalesforceStandardField label="Bank Name" value={profile.bank_name} />
                                        <SalesforceStandardField label="Account Number" value={profile.account_number} />
                                        <SalesforceStandardField label="Bank IFSC Code" value={profile.ifsc_code} />
                                        <SalesforceStandardField
                                            label="Total Compensation Yield (CTC)"
                                            value={profile.salary_details ? `₹${profile.salary_details}` : '—'}
                                            isSensitive
                                            isVisible={showSalary}
                                            onToggleVisibility={() => setShowSalary(!showSalary)}
                                        />
                                    </Grid>
                                </SalesforceCardBlock>

                                <SalesforceCardBlock title="Legal Compliance Classification Data">
                                    <Grid container spacing={3}>
                                        <SalesforceStandardField label="Pan Number" value={profile.pan_number} isCopyable onCopy={() => handleCopy(profile.pan_number, 'pan')} isCopied={copiedField === 'pan'} />
                                        <SalesforceStandardField label="Aadhaar Number" value="Identity Masked & Secured for Audit Records" isItalic />
                                    </Grid>
                                </SalesforceCardBlock>
                            </Stack>
                        )}
                    </Grid>

                    {/* RIGHT COLUMN SIDEBAR DETAILS */}
                    <Grid item xs={12} lg={4}>
                        <SalesforceCardBlock title="Provisioned Hardware & Network Interfaces">
                            <Stack spacing={3.5}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#444444', fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
                                        Laptop Assignment ID
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ borderBottom: '1px solid #f3f2f1', pb: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', flex: 1, color: '#181818' }}>
                                            {profile.laptop_serial_number || '—'}
                                        </Typography>
                                        {profile.laptop_serial_number && (
                                            <IconButton size="small" onClick={() => handleCopy(profile.laptop_serial_number, 'laptop')} sx={{ color: copiedField === 'laptop' ? '#2e7d32' : '#747474', p: 0.25 }}>
                                                {copiedField === 'laptop' ? <Check size={14} /> : <Copy size={14} />}
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Box>

                                <Box>
                                    <Typography variant="caption" sx={{ color: '#444444', fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
                                        Corporate Sim Number
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ borderBottom: '1px solid #f3f2f1', pb: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', flex: 1, color: '#181818' }}>
                                            {profile.corp_sim_number || '—'}
                                        </Typography>
                                        {profile.corp_sim_number && (
                                            <IconButton size="small" onClick={() => handleCopy(profile.corp_sim_number, 'sim')} sx={{ color: copiedField === 'sim' ? '#2e7d32' : '#747474', p: 0.25 }}>
                                                {copiedField === 'sim' ? <Check size={14} /> : <Copy size={14} />}
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                        </SalesforceCardBlock>
                    </Grid>

                </Grid>
            </Box>
        </Box>
    );
};


const SalesforceMiniField = ({ label, value, isCopyable, onCopy, isCopied }) => (
    <Grid item xs={12} sm={6} md={3} sx={{ borderRight: { md: '1px solid #dddbda' }, '&:last-child': { borderRight: 'none' } }}>
        <Typography variant="caption" sx={{ color: '#747474', fontWeight: 500, display: 'block', fontSize: '0.72rem' }}>
            {label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#181818', fontSize: '0.85rem', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                {value || '—'}
            </Typography>
            {isCopyable && value && (
                <IconButton size="small" onClick={onCopy} sx={{ p: 0.2, color: isCopied ? '#2e7d32' : '#747474' }}>
                    {isCopied ? <Check size={12} /> : <Copy size={12} />}
                </IconButton>
            )}
        </Stack>
    </Grid>
);

const SalesforceCardBlock = ({ title, children }) => (
    <Box sx={{ bgcolor: '#ffffff', border: '1px solid #dddbda', borderRadius: '4px', mb: 3, overflow: 'hidden', boxShadow: '0 2px 2px 0 rgba(0, 0, 0, 0.03)' }}>
        {/* Crisp Salesforce Title Block Banner */}
        <Box sx={{ bgcolor: '#f3f2f1', px: 2.5, py: 1.2, borderBottom: '1px solid #dddbda' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#081c3b', fontSize: '0.9rem' }}>
                {title}
            </Typography>
        </Box>
        {/* Details Data Presentation Body Canvas */}
        <Box sx={{ p: 2.5 }}>
            {children}
        </Box>
    </Box>
);

const SalesforceStandardField = ({ label, value, isCopyable, onCopy, isCopied, isSensitive, isVisible, onToggleVisibility, isItalic }) => (
    <Grid item xs={12} sm={6}>
        <Box sx={{ borderBottom: '1px solid #f3f2f1', pb: 1, minHeight: '44px' }}>
            <Typography variant="caption" sx={{ color: '#747474', fontWeight: 500, display: 'block', mb: 0.25, fontSize: '0.72rem' }}>
                {label}
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" sx={{
                    fontWeight: 500,
                    color: isItalic ? '#747474' : '#181818',
                    fontStyle: isItalic ? 'italic' : 'normal',
                    fontSize: '0.85rem',
                    fontFamily: isSensitive && !isVisible ? 'monospace' : 'inherit',
                    letterSpacing: isSensitive && !isVisible ? '0.12em' : 'normal'
                }}>
                    {isSensitive && !isVisible ? '••••••••••••' : (value || '—')}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {isSensitive && (
                        <IconButton size="small" onClick={onToggleVisibility} sx={{ color: '#747474', p: 0.2 }}>
                            {isVisible ? <EyeOff size={13} /> : <Eye size={13} />}
                        </IconButton>
                    )}
                    {isCopyable && value && (
                        <IconButton size="small" onClick={onCopy} sx={{ color: isCopied ? '#2e7d32' : '#747474', p: 0.2 }}>
                            {isCopied ? <Check size={13} /> : <Copy size={13} />}
                        </IconButton>
                    )}
                </Box>
            </Stack>
        </Box>
    </Grid>
);

export default MyProfile;